(() => {
  const GLOBAL_KEY = 'OtomaxMainMarketplaceV10';
  const INIT_ATTRIBUTE = 'data-otx-marketplace-v10-initialized';

  if (window[GLOBAL_KEY]?.initAll) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const initSection = (section) => {
    if (!section || section.hasAttribute(INIT_ATTRIBUTE)) return;
    section.setAttribute(INIT_ATTRIBUTE, 'true');

    // Cleanup markup left in the live Theme Editor DOM by older revisions.
    section.querySelectorAll('.otx-service-drawer__tools').forEach((element) => element.remove());

    const formatRupiah = (value) => {
      const amount = Math.max(0, Number(value) || 0);
      return `Rp${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(amount)}`;
    };

    const parseMoney = (value) => {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const digits = String(value || '').replace(/[^0-9]/g, '');
      return Number(digits || 0);
    };

    const slugify = (value) => String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const buildTargetUrl = (targetUrl, params) => {
      if (!targetUrl || targetUrl === '#') return null;
      const url = new URL(targetUrl, window.location.origin);

      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (Array.isArray(value)) {
          const cleanValues = value.map((item) => String(item ?? '').trim()).filter(Boolean);
          if (cleanValues.length) url.searchParams.set(key, cleanValues.join(','));
          return;
        }

        if (typeof value === 'object') {
          url.searchParams.set(key, JSON.stringify(value));
          return;
        }

        const cleanValue = String(value).trim();
        if (cleanValue) url.searchParams.set(key, cleanValue);
      });

      return url;
    };

    const portalId = `OtxDrawerPortal-${section.dataset.sectionId || section.id || 'main'}`;
    document.getElementById(portalId)?.remove();

    const portalRoot = document.createElement('div');
    portalRoot.id = portalId;
    portalRoot.className = 'otx-main-marketplace otx-drawer-portal';
    portalRoot.dataset.otxPortalFor = section.dataset.sectionId || section.id || '';
    portalRoot.setAttribute('aria-live', 'off');

    const sourceStyles = window.getComputedStyle(section);
    [
      '--otx-page-bg', '--otx-surface', '--otx-navy', '--otx-yellow',
      '--otx-yellow-hover', '--otx-text', '--otx-muted', '--otx-border',
      '--otx-success', '--otx-radius', '--otx-card-shadow'
    ].forEach((name) => {
      const value = sourceStyles.getPropertyValue(name);
      if (value) portalRoot.style.setProperty(name, value.trim());
    });
    portalRoot.style.fontFamily = sourceStyles.fontFamily;
    portalRoot.style.fontSize = sourceStyles.fontSize;
    portalRoot.dir = section.dir || document.documentElement.dir || 'ltr';
    document.body.appendChild(portalRoot);

    const portalizeDrawers = (...drawers) => {
      drawers.filter(Boolean).forEach((drawerElement) => portalRoot.appendChild(drawerElement));
    };

    const closeAllDrawers = (except = null) => {
      portalRoot.querySelectorAll('.otx-service-drawer.is-open, .otx-product-drawer.is-open').forEach((openDrawer) => {
        if (openDrawer === except) return;
        openDrawer.classList.remove('is-open');
        openDrawer.setAttribute('aria-hidden', 'true');
        openDrawer.hidden = true;
      });
    };

    /* ---------------------------------------------------------
       Hero slider
       --------------------------------------------------------- */
    const slider = section.querySelector('[data-hero-slider]');
    const track = section.querySelector('[data-slider-track]');
    const slides = [...section.querySelectorAll('[data-slide]')];
    const dots = [...section.querySelectorAll('[data-slider-dot]')];
    const prevButton = section.querySelector('[data-slider-prev]');
    const nextButton = section.querySelector('[data-slider-next]');

    let currentIndex = 0;
    let autoplayTimer = null;
    let touchStartX = 0;
    let touchEndX = 0;

    const autoplayEnabled = section.dataset.autoplay === 'true';
    const autoplaySpeed = Number(section.dataset.autoplaySpeed || 6000);

    const goToSlide = (index, userInitiated = false) => {
      if (!track || slides.length < 1) return;

      currentIndex = (index + slides.length) % slides.length;
      track.style.transform = `translate3d(-${currentIndex * 100}%, 0, 0)`;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));

        slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });

      if (userInitiated) restartAutoplay();
    };

    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => goToSlide(currentIndex + 1), autoplaySpeed);
    };

    const restartAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    prevButton?.addEventListener('click', () => goToSlide(currentIndex - 1, true));
    nextButton?.addEventListener('click', () => goToSlide(currentIndex + 1, true));
    dots.forEach((dot) => dot.addEventListener('click', () => goToSlide(Number(dot.dataset.sliderDot), true)));

    slider?.addEventListener('mouseenter', stopAutoplay);
    slider?.addEventListener('mouseleave', startAutoplay);
    slider?.addEventListener('focusin', stopAutoplay);
    slider?.addEventListener('focusout', startAutoplay);
    slider?.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0].screenX;
      stopAutoplay();
    }, { passive: true });
    slider?.addEventListener('touchend', (event) => {
      touchEndX = event.changedTouches[0].screenX;
      const distance = touchEndX - touchStartX;
      if (Math.abs(distance) > 45) goToSlide(distance > 0 ? currentIndex - 1 : currentIndex + 1, true);
      else startAutoplay();
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    /* ---------------------------------------------------------
       Service hub tabs
       --------------------------------------------------------- */
    const tabButtons = [...section.querySelectorAll('[data-booking-tab]')];
    const panels = [...section.querySelectorAll('[data-booking-panel]')];
    let syncProductFilterFields = () => {};

    panels.forEach((panel) => {
      panel.querySelectorAll('select, input, textarea, button').forEach((field) => {
        if ('required' in field && !field.dataset.originalRequired) {
          field.dataset.originalRequired = String(field.required);
        }
      });
    });

    const setPanelFieldsEnabled = (panel, enabled) => {
      panel.querySelectorAll('select, input, textarea, button').forEach((field) => {
        field.disabled = !enabled;
        if ('required' in field && field.dataset.originalRequired) {
          field.required = enabled && field.dataset.originalRequired === 'true';
        }
      });
    };

    const setBookingTab = (tabName, focusPanel = false) => {
      tabButtons.forEach((button) => {
        const active = button.dataset.bookingTab === tabName;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.setAttribute('tabindex', active ? '0' : '-1');
      });

      panels.forEach((panel) => {
        const active = panel.dataset.bookingPanel === tabName;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        setPanelFieldsEnabled(panel, active);

        if (active && tabName === 'product') {
          window.requestAnimationFrame(syncProductFilterFields);
        }

        if (active && focusPanel) {
          window.requestAnimationFrame(() => {
            panel.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus();
          });
        }
      });
    };

    tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => setBookingTab(button.dataset.bookingTab));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabButtons.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabButtons.length) % tabButtons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabButtons.length - 1;
        tabButtons[nextIndex].focus();
        setBookingTab(tabButtons[nextIndex].dataset.bookingTab);
      });
    });

    /* ---------------------------------------------------------
       Emergency services V5: compact summary + service drawer
       --------------------------------------------------------- */
    const emergencyForm = section.querySelector('[data-emergency-form]');
    const serviceCards = [...section.querySelectorAll('[data-service-card]')];
    const serviceCount = section.querySelector('[data-service-count]');
    const emergencyMessage = section.querySelector('[data-emergency-message]');
    const emergencySummaryLabel = section.querySelector('[data-emergency-summary-label]');
    const emergencyGrandTotal = section.querySelector('[data-emergency-grand-total]');
    const emergencySummaryList = section.querySelector('[data-emergency-summary-list]');
    const emergencySummaryEmpty = section.querySelector('[data-selected-services-empty]');
    const drawer = section.querySelector('[data-service-drawer]');
    const drawerPanel = drawer?.querySelector('.otx-service-drawer__panel');
    const drawerOpenButtons = [...section.querySelectorAll('[data-open-service-drawer]')];
    const drawerCloseButtons = [...section.querySelectorAll('[data-close-service-drawer]')];
    const drawerSaveButton = section.querySelector('[data-save-service-drawer]');
    drawer?.querySelector('.otx-service-drawer__tools')?.remove();
    const clearServicesButton = section.querySelector('[data-clear-services]');
    const drawerCount = section.querySelector('[data-drawer-count]');
    const drawerTotal = section.querySelector('[data-drawer-total]');
    const serviceSearch = section.querySelector('[data-service-search]');
    const serviceFilterButtons = [...section.querySelectorAll('[data-service-filter]')];
    const serviceNoResults = section.querySelector('[data-service-no-results]');
    let activeServiceFilter = 'all';
    let drawerReturnFocus = null;
    let drawerCloseTimer = null;

    const resolvePricingMode = (card) => {
      const mode = card.dataset.pricingMode || 'auto';
      if (mode !== 'auto') return mode;
      return parseMoney(card.dataset.additionalPrice) > 0 ? 'progressive' : 'per_unit';
    };

    const getCardQuantity = (card) => {
      const output = card.querySelector('[data-quantity-output]');
      return Math.max(1, Number(output?.value || output?.textContent || 1));
    };

    const setCardQuantity = (card, nextQuantity) => {
      const max = Math.max(1, Number(card.dataset.maxQuantity || 20));
      const quantity = Math.min(max, Math.max(1, Number(nextQuantity) || 1));
      const output = card.querySelector('[data-quantity-output]');
      const quantityInput = card.querySelector('[data-service-quantity-input]');
      const minus = card.querySelector('[data-quantity-minus]');
      const plus = card.querySelector('[data-quantity-plus]');

      if (output) {
        output.value = String(quantity);
        output.textContent = String(quantity);
      }
      if (quantityInput) quantityInput.value = String(quantity);
      if (minus) minus.disabled = quantity <= 1;
      if (plus) plus.disabled = quantity >= max;
      return quantity;
    };

    const calculateCard = (card) => {
      const checkbox = card.querySelector('[data-emergency-service]');
      const selected = Boolean(checkbox?.checked);
      const mode = resolvePricingMode(card);
      const basePrice = parseMoney(card.dataset.basePrice);
      const additionalPrice = parseMoney(card.dataset.additionalPrice);
      const quantity = setCardQuantity(card, getCardQuantity(card));
      const packageSize = Math.max(1, Number(card.dataset.packageSize || 1));
      const unitLabel = card.dataset.unitLabel || 'item';
      const packageUnitLabel = card.dataset.packageUnitLabel || unitLabel;

      let lineTotal = 0;
      if (mode === 'progressive') lineTotal = basePrice + Math.max(0, quantity - 1) * additionalPrice;
      else if (mode === 'fixed') lineTotal = basePrice;
      else lineTotal = basePrice * quantity;

      const totalUnits = mode === 'package' ? quantity * packageSize : quantity;
      const totalUnitLabel = mode === 'package' ? packageUnitLabel : unitLabel;
      const quantityPanel = card.querySelector('[data-service-quantity-panel]');
      const quantityInput = card.querySelector('[data-service-quantity-input]');
      const lineTotalInput = card.querySelector('[data-service-line-total-input]');
      const baseLabel = card.querySelector('[data-base-price-label]');
      const priceSuffix = card.querySelector('[data-service-price-suffix]');
      const packageNote = card.querySelector('[data-package-note]');
      const unitTotal = card.querySelector('[data-unit-total]');
      const lineTotalElement = card.querySelector('[data-line-total]');

      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-package', mode === 'package');
      card.classList.toggle('is-fixed', mode === 'fixed');
      if (quantityPanel) quantityPanel.hidden = !selected;
      if (quantityInput) quantityInput.disabled = !selected;
      if (lineTotalInput) {
        lineTotalInput.disabled = !selected;
        lineTotalInput.value = String(lineTotal);
      }

      if (baseLabel) baseLabel.textContent = formatRupiah(basePrice);
      if (priceSuffix) {
        if (mode === 'progressive' && additionalPrice > 0) {
          priceSuffix.textContent = `+${formatRupiah(additionalPrice)}/${unitLabel} berikutnya`;
        } else if (mode === 'package') {
          priceSuffix.textContent = `/ paket (${packageSize} ${packageUnitLabel})`;
        } else if (mode === 'per_unit') {
          priceSuffix.textContent = `/ ${unitLabel}`;
        } else {
          priceSuffix.textContent = '';
        }
      }

      if (packageNote) {
        packageNote.hidden = mode !== 'package';
        packageNote.textContent = mode === 'package'
          ? `Wajib kelipatan ${packageSize} ${packageUnitLabel} per paket`
          : '';
      }

      if (unitTotal) unitTotal.textContent = `${totalUnits} ${totalUnitLabel} total`;
      if (lineTotalElement) lineTotalElement.textContent = formatRupiah(lineTotal);

      return {
        selected,
        value: checkbox?.value || card.dataset.serviceValue || '',
        title: card.querySelector('.otx-service-card-v3__copy > strong')?.textContent?.trim() || '',
        quantity,
        totalUnits,
        unitLabel: totalUnitLabel,
        lineTotal,
        pricingMode: mode
      };
    };

    const renderEmergencySummary = (details) => {
      if (!emergencySummaryList || !emergencySummaryEmpty) return;
      emergencySummaryList.replaceChildren();
      emergencySummaryEmpty.hidden = details.length > 0;
      emergencySummaryList.hidden = details.length === 0;

      details.slice(0, 3).forEach((item) => {
        const row = document.createElement('div');
        row.className = 'otx-selected-services__row';

        const copy = document.createElement('span');
        const title = document.createElement('strong');
        const meta = document.createElement('small');
        const price = document.createElement('b');

        title.textContent = item.title;
        meta.textContent = `${item.totalUnits} ${item.unitLabel}`;
        price.textContent = formatRupiah(item.lineTotal);
        copy.append(title, meta);
        row.append(copy, price);
        emergencySummaryList.append(row);
      });

      if (details.length > 3) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'otx-selected-services__more';
        more.dataset.openServiceDrawer = '';
        more.textContent = `+${details.length - 3} layanan lainnya`;
        more.addEventListener('click', openServiceDrawer);
        emergencySummaryList.append(more);
      }
    };

    const updateEmergencySummary = () => {
      const details = serviceCards.map(calculateCard).filter((item) => item.selected);
      const total = details.reduce((sum, item) => sum + item.lineTotal, 0);
      const selectedLabel = details.length ? `${details.length} dipilih` : 'Belum dipilih';

      if (serviceCount) {
        serviceCount.textContent = selectedLabel;
        serviceCount.classList.toggle('has-selection', details.length > 0);
      }
      if (emergencySummaryLabel) {
        emergencySummaryLabel.textContent = details.length
          ? `${details.length} layanan dipilih`
          : 'Belum ada layanan';
      }
      if (emergencyGrandTotal) emergencyGrandTotal.textContent = formatRupiah(total);
      if (drawerCount) drawerCount.textContent = details.length ? `${details.length} layanan` : 'Belum dipilih';
      if (drawerTotal) drawerTotal.textContent = formatRupiah(total);
      if (drawerSaveButton) drawerSaveButton.textContent = details.length
        ? `Simpan ${details.length} Layanan`
        : 'Simpan Layanan';
      if (details.length && emergencyMessage) emergencyMessage.textContent = '';
      renderEmergencySummary(details);
      return { details, total };
    };

    function openServiceDrawer(event) {
      event?.preventDefault?.();
      if (!drawer || !drawerPanel) return;
      closeAllDrawers(drawer);
      document.documentElement.classList.remove('otx-product-drawer-open');
      document.body.classList.remove('otx-product-drawer-open');
      window.clearTimeout(drawerCloseTimer);
      drawerReturnFocus = event?.currentTarget || document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('otx-service-drawer-open');
      document.body.classList.add('otx-service-drawer-open');
      window.requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        drawerPanel.focus({ preventScroll: true });
      });
    }

    const closeServiceDrawer = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('otx-service-drawer-open');
      document.body.classList.remove('otx-service-drawer-open');
      drawerCloseTimer = window.setTimeout(() => {
        drawer.hidden = true;
        drawerReturnFocus?.focus?.({ preventScroll: true });
      }, 220);
    };

    const applyServiceFilters = () => {
      const query = serviceSearch?.value?.trim().toLowerCase() || '';
      let visibleCount = 0;
      serviceCards.forEach((card) => {
        const category = card.dataset.serviceCategory || 'lainnya';
        const haystack = card.textContent.toLowerCase();
        const categoryMatch = activeServiceFilter === 'all' || category === activeServiceFilter;
        const searchMatch = !query || haystack.includes(query);
        const visible = categoryMatch && searchMatch;
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });
      if (serviceNoResults) serviceNoResults.hidden = visibleCount > 0;
    };

    drawerOpenButtons.forEach((button) => button.addEventListener('click', openServiceDrawer));
    drawerCloseButtons.forEach((button) => button.addEventListener('click', closeServiceDrawer));
    drawerSaveButton?.addEventListener('click', closeServiceDrawer);

    clearServicesButton?.addEventListener('click', () => {
      serviceCards.forEach((card) => {
        const checkbox = card.querySelector('[data-emergency-service]');
        if (checkbox) checkbox.checked = false;
        setCardQuantity(card, 1);
      });
      updateEmergencySummary();
    });

    serviceFilterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeServiceFilter = button.dataset.serviceFilter || 'all';
        serviceFilterButtons.forEach((item) => item.classList.toggle('is-active', item === button));
        applyServiceFilters();
      });
    });
    serviceSearch?.addEventListener('input', applyServiceFilters);

    serviceCards.forEach((card) => {
      const checkbox = card.querySelector('[data-emergency-service]');
      const minus = card.querySelector('[data-quantity-minus]');
      const plus = card.querySelector('[data-quantity-plus]');

      checkbox?.addEventListener('change', () => {
        updateEmergencySummary();
        if (checkbox.checked) {
          window.requestAnimationFrame(() => card.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
        }
      });
      minus?.addEventListener('click', () => {
        setCardQuantity(card, getCardQuantity(card) - 1);
        updateEmergencySummary();
      });
      plus?.addEventListener('click', () => {
        setCardQuantity(card, getCardQuantity(card) + 1);
        updateEmergencySummary();
      });
    });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeServiceDrawer();
        return;
      }
      if (event.key !== 'Tab' || !drawerPanel) return;
      const focusable = [...drawerPanel.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const dateInput = section.querySelector('[data-booking-date]');
    if (dateInput) {
      const today = new Date();
      const timezoneOffset = today.getTimezoneOffset() * 60000;
      dateInput.min = new Date(today.getTime() - timezoneOffset).toISOString().split('T')[0];
    }

    emergencyForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      const { details, total } = updateEmergencySummary();

      if (!details.length) {
        if (emergencyMessage) emergencyMessage.textContent = 'Pilih minimal satu layanan terlebih dahulu.';
        openServiceDrawer(event);
        return;
      }
      if (!emergencyForm.reportValidity()) return;

      const locationField = emergencyForm.querySelector('[name="location"]');
      const dateField = emergencyForm.querySelector('[name="date"]');
      const action = emergencyForm.getAttribute('action');
      const target = action && action !== '#' ? action : section.dataset.bookingFallbackUrl;
      const url = buildTargetUrl(target, {
        type: 'emergency',
        service: details[0].value,
        services: details.map((item) => item.value),
        service_details: details,
        grand_total: total,
        location: locationField?.value,
        date: dateField?.value
      });

      if (url) window.location.href = url.toString();
      else if (emergencyMessage) emergencyMessage.textContent = 'Link halaman booking belum diatur di Theme Editor.';
    });

    applyServiceFilters();
    updateEmergencySummary();

    /* ---------------------------------------------------------
       Product consultant: automatic, category-aware flow
       --------------------------------------------------------- */
    const productForm = section.querySelector('[data-product-form]');
    const productStages = [...section.querySelectorAll('[data-product-stage]')];
    const needFields = [...section.querySelectorAll('[data-product-need]')];
    const filterGroups = [...section.querySelectorAll('[data-filter-group]')];
    const productMessage = section.querySelector('[data-product-message]');
    const summaryBar = section.querySelector('[data-product-summary-bar]');
    const selectedNeedText = section.querySelector('[data-selected-need-text]');
    const selectedVehicleText = section.querySelector('[data-selected-vehicle-text]');
    const dynamicFilterTitle = section.querySelector('[data-dynamic-filter-title]');
    const submitLabel = section.querySelector('[data-product-submit-label]');
    const vehicleFields = [...section.querySelectorAll('[data-auto-vehicle-field]')];
    const productDrawer = section.querySelector('[data-product-drawer]');
    const productDrawerPanel = productDrawer?.querySelector('.otx-product-drawer__panel');
    const productDrawerOpenButtons = [...section.querySelectorAll('[data-open-product-drawer]')];
    const productDrawerCloseButtons = [...section.querySelectorAll('[data-close-product-drawer]')];

    portalizeDrawers(drawer, productDrawer);

    const compactNeedText = section.querySelector('[data-product-compact-need]');
    const compactVehicleText = section.querySelector('[data-product-compact-vehicle]');
    let productDrawerReturnFocus = null;
    let productDrawerCloseTimer = null;
    let currentProductStage = 'need';
    let autoAdvanceTimer = null;

    const openProductDrawer = (event, stageName = null) => {
      event?.preventDefault?.();
      if (!productDrawer || !productDrawerPanel) return;
      closeAllDrawers(productDrawer);
      document.documentElement.classList.remove('otx-service-drawer-open');
      document.body.classList.remove('otx-service-drawer-open');
      window.clearTimeout(productDrawerCloseTimer);
      productDrawerReturnFocus = event?.currentTarget || document.activeElement;
      productDrawer.hidden = false;
      productDrawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('otx-product-drawer-open');
      document.body.classList.add('otx-product-drawer-open');
      if (stageName) showProductStage(stageName);
      window.requestAnimationFrame(() => {
        productDrawer.classList.add('is-open');
        productDrawerPanel.focus({ preventScroll: true });
      });
    };

    const closeProductDrawer = () => {
      if (!productDrawer) return;
      productDrawer.classList.remove('is-open');
      productDrawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('otx-product-drawer-open');
      document.body.classList.remove('otx-product-drawer-open');
      productDrawerCloseTimer = window.setTimeout(() => {
        productDrawer.hidden = true;
        productDrawerReturnFocus?.focus?.({ preventScroll: true });
      }, 220);
    };

    const getSelectedNeed = () => productForm?.querySelector('[data-product-need]:checked') || null;

    const updateNeedCards = () => {
      needFields.forEach((field) => {
        field.closest('.otx-auto-need-option')?.classList.toggle('is-selected', field.checked);
      });
      const selected = getSelectedNeed();
      if (selectedNeedText) selectedNeedText.textContent = selected?.value || 'Belum dipilih';
      if (compactNeedText) compactNeedText.textContent = selected?.value || 'Belum dipilih';
      if (selected && productMessage) productMessage.textContent = '';
      return selected;
    };

    const vehicleData = () => ({
      brand: productForm?.elements.namedItem('brand')?.value?.trim() || '',
      model: productForm?.elements.namedItem('model')?.value?.trim() || '',
      year: productForm?.elements.namedItem('year')?.value?.trim() || '',
      variant: productForm?.elements.namedItem('variant')?.value?.trim() || ''
    });

    const vehicleIsComplete = () => {
      const data = vehicleData();
      return data.brand.length >= 2 && data.model.length >= 2 && Boolean(data.year);
    };

    const updateVehicleSummary = () => {
      const data = vehicleData();
      const text = [data.brand, data.model, data.year].filter(Boolean).join(' · ') || 'Belum diisi';
      if (selectedVehicleText) selectedVehicleText.textContent = text;
      if (compactVehicleText) compactVehicleText.textContent = text;
      return data;
    };

    const activeNeedHandle = () => slugify(getSelectedNeed()?.value || 'generic');

    const setFilterGroup = () => {
      const needHandle = activeNeedHandle();
      const matchingGroup = filterGroups.find((group) => group.dataset.filterGroup === needHandle)
        || filterGroups.find((group) => group.dataset.filterGroup === 'generic');

      filterGroups.forEach((group) => {
        const active = group === matchingGroup;
        group.hidden = !active;
        group.querySelectorAll('input, select, textarea, button').forEach((field) => {
          field.disabled = !active;
        });
      });

      const selectedValue = getSelectedNeed()?.value || 'Produk';
      if (dynamicFilterTitle) dynamicFilterTitle.textContent = `Filter ${selectedValue}`;
      if (submitLabel) submitLabel.textContent = needHandle === 'belum-yakin'
        ? 'Minta Rekomendasi'
        : `Temukan ${selectedValue}`;
    };

    syncProductFilterFields = setFilterGroup;

    const showProductStage = (stageName, focusFirst = false) => {
      currentProductStage = stageName;
      productStages.forEach((stage) => {
        const active = stage.dataset.productStage === stageName;
        stage.hidden = !active;
        stage.classList.toggle('is-active', active);
      });

      if (summaryBar) summaryBar.hidden = stageName !== 'filters';
      if (stageName === 'filters') setFilterGroup();

      if (focusFirst) {
        window.requestAnimationFrame(() => {
          productStages.find((stage) => stage.dataset.productStage === stageName)
            ?.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus();
        });
      }
    };

    const scheduleVehicleAdvance = (delay = 700) => {
      window.clearTimeout(autoAdvanceTimer);
      if (!vehicleIsComplete()) return;
      autoAdvanceTimer = window.setTimeout(() => {
        updateVehicleSummary();
        showProductStage('filters', true);
      }, delay);
    };

    needFields.forEach((field) => {
      field.addEventListener('change', () => {
        const selected = updateNeedCards();
        if (!selected) return;
        window.clearTimeout(autoAdvanceTimer);
        autoAdvanceTimer = window.setTimeout(() => {
          showProductStage('vehicle', true);
          if (vehicleIsComplete()) scheduleVehicleAdvance(450);
        }, 220);
      });
    });

    vehicleFields.forEach((field) => {
      field.addEventListener('input', () => {
        updateVehicleSummary();
        scheduleVehicleAdvance(950);
      });
      field.addEventListener('change', () => {
        updateVehicleSummary();
        scheduleVehicleAdvance(350);
      });
      field.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (vehicleIsComplete()) showProductStage('filters', true);
        else field.blur();
      });
    });

    section.querySelectorAll('[data-edit-product-need]').forEach((button) => {
      button.addEventListener('click', (event) => {
        openProductDrawer(event, 'need');
        showProductStage('need', true);
      });
    });

    section.querySelectorAll('[data-edit-vehicle]').forEach((button) => {
      button.addEventListener('click', (event) => {
        openProductDrawer(event, 'vehicle');
        showProductStage('vehicle', true);
      });
    });

    productDrawerOpenButtons.forEach((button) => {
      button.addEventListener('click', (event) => openProductDrawer(event));
    });
    productDrawerCloseButtons.forEach((button) => button.addEventListener('click', closeProductDrawer));

    productDrawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeProductDrawer();
        return;
      }
      if (event.key !== 'Tab' || !productDrawerPanel) return;
      const focusable = [...productDrawerPanel.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const unknownTireSize = productForm?.elements.namedItem('unknown_tire_size');
    const tireSizeField = productForm?.elements.namedItem('tire_size');
    unknownTireSize?.addEventListener('change', () => {
      if (!tireSizeField) return;
      tireSizeField.disabled = unknownTireSize.checked;
      if (unknownTireSize.checked) tireSizeField.value = '';
    });

    productForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      const selectedNeed = updateNeedCards();

      if (!selectedNeed) {
        if (productMessage) productMessage.textContent = 'Pilih produk yang ingin dicari.';
        showProductStage('need', true);
        return;
      }

      if (!vehicleIsComplete()) {
        showProductStage('vehicle', true);
        const firstInvalid = vehicleFields.find((field) => !field.value?.trim());
        firstInvalid?.focus();
        firstInvalid?.reportValidity?.();
        return;
      }

      setFilterGroup();
      const formData = new FormData(productForm);
      const params = Object.fromEntries(formData.entries());
      const needHandle = slugify(params.need || '');

      const searchQuery = [
        params.need,
        params.brand,
        params.model,
        params.year,
        params.variant,
        params.tire_size,
        params.tire_brand,
        params.ring_size,
        params.pcd,
        params.wheel_brand,
        params.viscosity,
        params.engine_type,
        params.battery_type,
        params.brake_part,
        params.suspension_type,
        params.accessory_keyword,
        params.keyword,
        params.problem
      ].filter(Boolean).join(' ');

      try {
        window.localStorage.setItem('otxVehicleProfile', JSON.stringify({
          need: params.need || '',
          brand: params.brand || '',
          model: params.model || '',
          year: params.year || '',
          variant: params.variant || '',
          plate: params.plate || ''
        }));
      } catch (error) {
        // Storage can be blocked; product search still works.
      }

      const action = productForm.getAttribute('action');
      const target = action && action !== '#' ? action : section.dataset.productFallbackUrl;
      const url = buildTargetUrl(target, {
        type: 'product-consultant',
        category: needHandle,
        q: searchQuery,
        sort_by: params.sort || 'relevance',
        ...params
      });

      if (url) window.location.href = url.toString();
    });

    try {
      const savedVehicle = JSON.parse(window.localStorage.getItem('otxVehicleProfile') || 'null');
      if (savedVehicle && productForm) {
        Object.entries(savedVehicle).forEach(([name, value]) => {
          if (!value) return;
          const field = productForm.elements.namedItem(name);
          if (!field) return;
          if (field instanceof RadioNodeList) {
            [...field].forEach((option) => { option.checked = option.value === value; });
          } else {
            field.value = value;
          }
        });
      }
    } catch (error) {
      // Ignore unavailable or malformed storage.
    }

    updateNeedCards();
    updateVehicleSummary();
    showProductStage('need');

    const initialTab = tabButtons.find((button) => button.classList.contains('is-active'))?.dataset.bookingTab
      || tabButtons[0]?.dataset.bookingTab;
    if (initialTab) setBookingTab(initialTab);

    goToSlide(0);
    startAutoplay();
  };

  const initAll = (root = document) => {
    if (root.matches?.('.otx-main-marketplace:not(.otx-drawer-portal)')) {
      initSection(root);
      return;
    }
    root.querySelectorAll?.('.otx-main-marketplace:not(.otx-drawer-portal)').forEach(initSection);
  };

  window[GLOBAL_KEY] = { initAll, initSection };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.('.otx-main-marketplace:not(.otx-drawer-portal)')
      ? event.target
      : event.target.querySelector?.('.otx-main-marketplace:not(.otx-drawer-portal)');
    initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.target?.querySelector?.('.otx-main-marketplace')?.dataset?.sectionId
      || event.target?.dataset?.sectionId;
    if (!sectionId) return;
    document.getElementById(`OtxDrawerPortal-${sectionId}`)?.remove();
    document.documentElement.classList.remove('otx-service-drawer-open', 'otx-product-drawer-open');
    document.body.classList.remove('otx-service-drawer-open', 'otx-product-drawer-open');
  });
})();
